import React, { useState } from 'react';
import {
    faAngleDown,
    faAngleUp,
    faTrash,
    faEdit,
    faAngleRight,
    faExpand
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Button,
    Modal,
    Form,
    Image
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import YouTube from 'react-youtube';

import config from '../config/config';
import { useResto } from '../contexts/RestoContext';
import { useMode } from '../contexts/ModeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ucfirst } from '../utils/utils';

function MediaItem({
    media,
    handleDelete,
    handleEdit,
    handleRankingUpdate,
    isFirst,
    isLast,
    isShortLine = true
}) {
    const { t } = useTranslation();
    const { isModeAdmin } = useMode();
    const { restoData } = useResto();
    const { activeLanguage } = useLanguage();

    const availableLanguages = restoData?.availableLanguages || [];

    const languageKey = ucfirst(activeLanguage || '');
    const label = media?.[`label${languageKey}`] || '';
    const details = media?.[`details${languageKey}`] || '';

    const getPhotoUrl = (media, format) =>
        media?.uuid
            ? `${config.documentsUrl}photos/${format}/${media.uuid}.jpg`
            : '';

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showFullScreen, setShowFullScreen] = useState(false);

    const [editedMedia, setEditedMedia] =
        useState({ ...(media || {}) });

    const handleTrashClick = () => setShowConfirmation(true);
    const handleCloseConfirmation = () => setShowConfirmation(false);

    const handleConfirmDelete = () => {
        if (media?.id) handleDelete(media.id);
        setShowConfirmation(false);
    };

    const handleRanking = (direction) => {
        if (media?.id && handleRankingUpdate) {
            handleRankingUpdate(direction, media.id);
        }
    };

    const handleEditClick = () => {
        setEditedMedia({ ...(media || {}) });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => setShowEditModal(false);

    const handleEditSubmit = () => {
        handleEdit(editedMedia);
        setShowEditModal(false);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditedMedia(prev => ({
            ...prev,
            [name]: value || ''
        }));
    };

    const toggleDetails = () =>
        setShowDetails(prev => !prev);

    const toggleFullScreen = () =>
        setShowFullScreen(prev => !prev);

    const extractVideoId = (url = '') => {
        const match = url.match(/[?&]v=([^&#]*)/);
        return match?.[1] || null;
    };

    const opts = {
        playerVars: { autoplay: 0 }
    };

    return (
        <div className="media-container">

            <div className="text-center">

                {/* PHOTO */}
                {media?.mediaType === 'photo' && (
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '20px' }}>
                        <img
                            src={getPhotoUrl(media, 'medium')}
                            alt={label}
                        />

                        <button
                            className="btn btn-primary btn-sm"
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px'
                            }}
                            onClick={toggleFullScreen}
                        >
                            <FontAwesomeIcon icon={faExpand} />
                        </button>
                    </div>
                )}

                {/* VIDEO */}
                {media?.mediaType === 'video' && (
                    <div className="video-container">
                        {extractVideoId(media?.videoUrl) && (
                            <YouTube
                                videoId={extractVideoId(media.videoUrl)}
                                opts={opts}
                                className="react-player"
                            />
                        )}
                    </div>
                )}
            </div>

            {/* ADMIN UI */}
            {isModeAdmin && (
                <>
                    <div className="media-label" style={{ marginTop: '10px' }}>
                        <strong><i>{label}</i></strong>

                        {details && (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={toggleDetails}
                            >
                                {t('DETAILS')}{' '}
                                <FontAwesomeIcon
                                    icon={showDetails ? faAngleDown : faAngleRight}
                                />
                            </button>
                        )}

                        <Button
                            variant="primary"
                            className="btn-sm"
                            onClick={handleEditClick}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>

                        {handleRankingUpdate && !isFirst && (
                            <Button
                                variant="primary"
                                className="btn-sm"
                                onClick={() => handleRanking('up')}
                            >
                                <FontAwesomeIcon icon={faAngleUp} />
                            </Button>
                        )}

                        {handleRankingUpdate && !isLast && (
                            <Button
                                variant="primary"
                                className="btn-sm"
                                onClick={() => handleRanking('down')}
                            >
                                <FontAwesomeIcon icon={faAngleDown} />
                            </Button>
                        )}

                        <Button
                            variant="primary"
                            className="btn-sm"
                            onClick={handleTrashClick}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>

                    {showDetails && (
                        <div className="kubuni-frame">
                            {details}
                        </div>
                    )}
                </>
            )}

            {/* FULLSCREEN */}
            {showFullScreen && (
                <div
                    className="full-screen-overlay"
                    onClick={toggleFullScreen}
                >
                    <img
                        src={getPhotoUrl(media, 'raw')}
                        alt={label}
                        className="full-screen-image"
                    />
                </div>
            )}

            {/* EDIT MODAL */}
            <Modal
                show={showEditModal}
                onHide={handleCloseEditModal}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('EDIT')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        {availableLanguages.map((language) => (
                            <div key={language}>
                                <Form.Group>
                                    <Form.Label>
                                        <Image
                                            src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                            className="flag-label"
                                            alt={language}
                                        />
                                    </Form.Label>

                                    <Form.Control
                                        type="text"
                                        name={`label${ucfirst(language)}`}
                                        value={
                                            editedMedia?.[`label${ucfirst(language)}`] || ''
                                        }
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>
                            </div>
                        ))}
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditModal}>
                        {t('CANCEL')}
                    </Button>

                    <Button variant="primary" onClick={handleEditSubmit}>
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
}

export default MediaItem;